import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router";

// @material-ui/core components
import { withStyles } from "tss-react/mui";
import { Add, Cloud, Delete, FreeBreakfast, Print, RestoreFromTrash } from "@mui/icons-material";

import Button from "../../components/CustomButtons/Button";
import VestigingenFilter, { getVestigingFilterState } from "../../components/VestigingFilter/VestigingFilter";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";

import MainNavbar from "../../components/Navbars/MainNavbar";
import tafelStyle from "../../assets/jss/material-dashboard-pro-react/views/tafelStyle";
import TranslationWrapper from "../../components/TranslationWrapper/TranslationWrapper";
import AuthenticationWrapper from "../../auth/AuthenticationWrapper";
import { Collapse, IconButton, TableCell, TextField, Tooltip } from "@mui/material";
import AreYouSureDialog from "../../components/Dialogs/AreYouSureDialog";
import { _delete, _post } from "../../http";
import JamezzTableV2, { useReloadState } from "../../components/JamezzTable/V2/JamezzTableV2";
import FormElement from "../../components/JamezzTable/FormElement";
import Edit from "@mui/icons-material/Edit";
import { useDialog } from "../../licensing/utils/DialogProvider";
import useJTFilter from "../../components/JamezzTable/V2/useJTFilter";

const endPoint = "core/printers";

function Index(props) {
  const [canCreateNieuwItem, setCanCreateNieuwItem] = useState(false);
  const { reloadState, updateReloadState } = useReloadState();
  const [openDialog] = useDialog();
  const { classes } = props;
  const [textField, searchFilter] = useJTFilter("name");

  useEffect(() => {
    setCanCreateNieuwItem(!!props.vestiging_id);
  }, [props.vestiging_id]);

  const handleStatus = (item) => {
    if (item.errorState) {
      return item.localPrinterIP ? <Print style={{ color: "RED" }} /> : <Cloud style={{ color: "RED" }} />;
    } else {
      return item.localPrinterIP ? <Print style={{ color: "GREEN" }} /> : <Cloud style={{ color: "GREEN" }} />;
    }
  };

  const filters = useMemo(() => {
    let filters = {};
    if (searchFilter) {
      filters = searchFilter;
    }
    if (props.filters) {
      filters = { ...filters, ...props.filters };
    }
    return filters;
  }, [searchFilter, props.filters]);

  const renderHeaderCells = useCallback(() => [
    { id: "id", label: "#", numeric: true, disablePadding: false, sortable: true },
    {
      id: "status",
      label: <TranslationWrapper tDefault={"Status"} tKey={"printers.status"} />,
      numeric: false,
      disablePadding: false,
      sortable: false,
    },
    {
      id: "name",
      label: <TranslationWrapper tDefault={"Printer name"} tKey={"printers.name"} />,
      numeric: false,
      disablePadding: false,
      sortable: true,
    },
    {
      id: "mac",
      label: <TranslationWrapper tDefault={"MAC/IP"} tKey={"printers.macip"} />,
      numeric: false,
      disablePadding: false,
      sortable: true,
    },
    {
      id: "salesarea",
      label: <TranslationWrapper tDefault={"Sales Area"} tKey={"printers.salesarea"} />,
      numeric: false,
      disablePadding: false,
      sortable: false,
    },
  ]);

  const renderRowCells = useCallback((row, isEditable) => {
    return (
      <>
        <TableCell>{row.id}</TableCell>
        <TableCell>{handleStatus(row)}</TableCell>
        <TableCell>
          {isEditable ? (
            <FormElement endPoint={endPoint} name={"name"} row={row}>
              <TextField size={"small"} variant={"standard"} />
            </FormElement>
          ) : null}
          {!isEditable ? row.name : null}
        </TableCell>
        <TableCell>{row.localPrinterIP ?? row.mac}</TableCell>
        <TableCell>{row.vestiging_id}</TableCell>
      </>
    );
  });

  const renderRowActions = useCallback((row, tableOptions) => {
    return (
      <TableCell align={"right"}>
        <Link to={`./${row.id}/edit`}>
          <Tooltip
            classes={{ tooltip: classes.tooltip }}
            placement={"bottom"}
            title={<TranslationWrapper tDefault={"Edit"} tKey={"global.jamezzTable.edit"} />}
          >
            <IconButton color={"primary"} simple size={"small"}>
              <Edit />
            </IconButton>
          </Tooltip>
        </Link>

        <AuthenticationWrapper permissionKey={"printers.delete"}>
          {tableOptions.showArchive ? (
            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              placement={"bottom"}
              title={<TranslationWrapper tDefault={"Herstel"} tKey={"global.jamezzTable.restore"} />}
            >
              <Button
                className={classes.actionButton}
                color={"success"}
                onClick={() => {
                  openDialog({
                    children: (
                      <AreYouSureDialog
                        content={
                          <TranslationWrapper
                            tDefault={"Restore? Are you sure you want to restore this item?"}
                            tKey={"global.jamezztable.confirm.restore.product"}
                          />
                        }
                        onOk={() => {
                          _post(`/core/printers/${row.id}/restore`).finally(updateReloadState);
                        }}
                      />
                    ),
                  });
                }}
                simple
              >
                <RestoreFromTrash className={classes.icon} />
              </Button>
            </Tooltip>
          ) : (
            <></>
          )}
          {!tableOptions.showArchive ? (
            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              placement={"bottom"}
              title={<TranslationWrapper tDefault={"Verwijderen"} tKey={"global.jamezzTable.remove"} />}
            >
              <Button
                className={classes.actionButton}
                color={"danger"}
                onClick={() => {
                  openDialog({
                    children: (
                      <AreYouSureDialog
                        content={
                          <TranslationWrapper
                            tDefault={"Delete? Are you sure you want to delete this item?"}
                            tKey={"global.jamezztable.confirm.delete.product"}
                          />
                        }
                        onOk={() => {
                          _delete("/core/printers/" + row.id).finally(updateReloadState);
                        }}
                      />
                    ),
                  });
                }}
                simple
              >
                <Delete className={classes.icon} />
              </Button>
            </Tooltip>
          ) : (
            <></>
          )}
        </AuthenticationWrapper>
      </TableCell>
    );
  });

  const renderSelectionActions = useCallback((selected, tableOptions) => {
    return (
      <AuthenticationWrapper permissionKey={"printers.multidelete"}>
        <>
          <Tooltip title={<TranslationWrapper tKey={"Test"} />}>
            <Collapse in={!tableOptions.showArchive}>
              <IconButton
                onClick={() => {
                  openDialog({
                    children: (
                      <AreYouSureDialog
                        content={
                          <TranslationWrapper
                            tKey={"Are you sure you want to run a test print on the selected printers?"}
                          />
                        }
                        onOk={() => {
                          _post("/core/printers/multipletest", { ids: selected }).then(() => {
                            updateReloadState();
                          });
                        }}
                      />
                    ),
                  });
                }}
                style={{ color: "green" }}
              >
                <Print />
              </IconButton>
            </Collapse>
          </Tooltip>

          <Tooltip title={<TranslationWrapper tKey={"Delete"} />}>
            <Collapse in={!tableOptions.showArchive}>
              <IconButton
                onClick={() => {
                  openDialog({
                    children: (
                      <AreYouSureDialog
                        content={<TranslationWrapper tKey={"Are you sure you want to delete the selected items?"} />}
                        onOk={() => {
                          _post("/core/printers/multipledelete", { ids: selected }).then(() => {
                            updateReloadState();
                          });
                        }}
                      />
                    ),
                  });
                }}
                style={{ color: "lightcoral" }}
              >
                <Delete />
              </IconButton>
            </Collapse>
          </Tooltip>
          <Tooltip title={<TranslationWrapper tKey={"Restore"} />}>
            <Collapse in={tableOptions.showArchive}>
              <IconButton
                onClick={() => {
                  openDialog({
                    children: (
                      <AreYouSureDialog
                        content={<TranslationWrapper tKey={"Are you sure you want to restore the selected items?"} />}
                        onOk={() => {
                          _post("/core/printers/multipleRestore", { ids: selected }).then(() => {
                            updateReloadState();
                          });
                        }}
                      />
                    ),
                  });
                }}
                style={{ color: "#66bb6a" }}
              >
                <RestoreFromTrash />
              </IconButton>
            </Collapse>
          </Tooltip>
        </>
      </AuthenticationWrapper>
    );
  }, []);

  return (
    <div>
      <MainNavbar paginaTitel={<TranslationWrapper tDefault={"Printers overzicht"} tKey={"printers.pagetitle"} />} />
      <GridContainer>
        {canCreateNieuwItem ? (
          <GridItem xs={12}>
            <Link to={"./create"}>
              <Button className={props.classes.marginRight} color={"success"}>
                <Add className={props.classes.icons} />{" "}
                <TranslationWrapper tDefault={"New printer"} tKey={"printers.new.item"} />
              </Button>
            </Link>
          </GridItem>
        ) : null}
      </GridContainer>

      <GridContainer>
        <VestigingenFilter />
      </GridContainer>
      <GridContainer>
        <GridItem xs={12}>
          <JamezzTableV2
            checkboxSelection={true}
            delete={"printers.delete"}
            dense={true}
            showRefresh={true}
            endPoint={endPoint}
            filters={filters}
            headerIcon={<FreeBreakfast />}
            reloadState={reloadState}
            renderCellsForRow={renderRowCells}
            renderHeaderCells={renderHeaderCells}
            renderRowActions={renderRowActions}
            renderSelectionActions={renderSelectionActions}
            tableTitle={<TranslationWrapper tDefault={"Printers"} tKey={"printers.table.title"} />}
            uuid={"Printers-c79553e6a2a6bda"}
          />
        </GridItem>
      </GridContainer>
    </div>
  );
}

// REDUX
const mapStateToProps = (state) => {
  return {
    filters: getVestigingFilterState(state, true),
    vestiging_id: state.filtersV2.vestiging_id?.value,
  };
};

export default connect(mapStateToProps)(withStyles(Index, tafelStyle));
